"use client";

import React from "react";
import { MapPin, Clock, Route, Percent, Cpu, ArrowRight, ShieldCheck, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hotspots = [{
  id: 1,
  top: "25%",
  left: "30%",
  label: "Current Flight Path: Proactive",
  iconName: "user",
  delay: "0ms"
}, {
  id: 2,
  top: "65%",
  left: "75%",
  label: "Critical Decision Point",
  iconName: "shop",
  delay: "300ms"
}, {
  id: 3,
  top: "45%",
  left: "55%",
  label: "Optimal Planning Window",
  iconName: "route",
  delay: "600ms"
}];
const backendFeatures = [{
  id: 1,
  title: "Asset Control",
  description: "Maintain full command of your home equity and aircraft. Our structured plans ensure you dictate the terms of your aging-in-place strategy.",
  iconName: "cpu"
}, {
  id: 2,
  title: "Facility Risk Mitigation",
  description: "Avoid the 'forced landing' of a nursing home. We deploy resources early to harden your home environment against medical cessation triggers.",
  iconName: "percent"
}, {
  id: 3,
  title: "Flight Plan Security",
  description: "A dedicated Care Advisor orchestrates your daily operations, ensuring your medical and logistical needs are met without compromising independence.",
  iconName: "shield"
}];
const getHotspotIcon = (name: string) => {
  switch (name) {
    case "user":
      return <MapPin className="size-4 text-primary-foreground" />;
    case "shop":
      return <Clock className="size-4 text-primary-foreground" />;
    case "route":
      return <Route className="size-4 text-primary-foreground" />;
    default:
      return <MapPin className="size-4 text-primary-foreground" />;
  }
};
const getFeatureIcon = (name: string) => {
  switch (name) {
    case "cpu":
      return <Cpu className="size-5 text-primary" />;
    case "percent":
      return <Percent className="size-5 text-primary" />;
    case "shield":
      return <ShieldCheck className="size-5 text-primary" />;
    default:
      return <Cpu className="size-5 text-primary" />;
  }
};
export default function ThePlanningWindow() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="240386" id="the-planning-window" className="relative w-full bg-background py-24 overflow-hidden text-foreground">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-md bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-md bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-4 uppercase">
            The Planning Window: <br className="hidden sm:block" />
            <span className="text-primary">Proactive vs. Crisis</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl">
            In aviation, the earlier you identify a deviation, the simpler the correction. Aging requires the same technical precision. Secure your home and your legacy before the window closes.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-8 relative rounded-md overflow-hidden border border-secondary bg-card shadow-xl group h-[400px] sm:h-[500px] lg:h-[600px]">
            {}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000 group-hover:scale-105" style={{
            backgroundImage: "url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_instrumentation_map.png')"
          }}>
              {}
              <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/60 to-primary/20 mix-blend-overlay" />
              
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
            </div>

            <TooltipProvider delayDuration={100}>
              {hotspots.map((hotspot, index) => <div key={hotspot.id} data-index={index} className="absolute z-20" style={{
              top: hotspot.top,
              left: hotspot.left
            }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative flex items-center justify-center group/hotspot outline-none">
                        <span className="absolute inline-flex h-full w-full rounded-md bg-primary opacity-40 animate-ping" style={{
                      animationDelay: hotspot.delay
                    }} />
                        <span className="relative inline-flex rounded-md size-10 bg-primary items-center justify-center shadow-lg border-2 border-background transition-transform duration-300 group-hover/hotspot:scale-110">
                          {getHotspotIcon(hotspot.iconName)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="font-mono font-medium shadow-2xl border border-primary/20 bg-secondary text-foreground">
                      <p>{hotspot.label}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>)}
            </TooltipProvider>

            {}
            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
              <div className="bg-secondary/90 backdrop-blur-md border border-border rounded-md p-3 shadow-md pointer-events-auto">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground font-mono">
                  <div className="size-2 rounded-md bg-chart-3 animate-pulse" />
                  STATUS: PROACTIVE PLANNING ACTIVE
                </div>
              </div>
              <div className="hidden sm:flex flex-col gap-2 pointer-events-auto">
                <Button variant="secondary" size="icon" className="rounded-md shadow-md bg-secondary/90 backdrop-blur hover:bg-secondary h-10 w-10 border border-border">
                  <Plus className="size-5" />
                  <span className="sr-only">Zoom In</span>
                </Button>
                <Button variant="secondary" size="icon" className="rounded-md shadow-md bg-secondary/90 backdrop-blur hover:bg-secondary h-10 w-10 border border-border">
                  <Minus className="size-5" />
                  <span className="sr-only">Zoom Out</span>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-card/60 backdrop-blur-xl border border-secondary rounded-md p-6 sm:p-8 shadow-2xl relative overflow-hidden">
              {}
              <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
              
              <h3 className="text-xl font-bold font-default text-foreground mb-6 flex items-center gap-2 uppercase tracking-tight">
                <Cpu className="size-5 text-primary" />
                Mission Parameters
              </h3>
              
              <div className="space-y-8 relative z-10">
                {backendFeatures.map((feature, index) => <div key={feature.id} data-index={index} className="flex gap-4 group">
                    <div className="shrink-0 mt-1">
                      <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center border border-primary/20 transition-colors duration-300 group-hover:bg-primary/20 group-hover:border-primary/40">
                        {getFeatureIcon(feature.iconName)}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold font-default text-foreground mb-1 uppercase">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-muted-foreground font-default leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="mt-8 pt-6 border-t border-border/50 relative z-10">
                <Button className="w-full group bg-primary text-primary-foreground hover:opacity-90 rounded-md font-default uppercase tracking-wider" size="lg" asChild>
                  <a href="/TheFlightPlanPage">
                    Review The Flight Plan
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}